import type {
  ResultListState,
  ResultListStateQuery,
} from '../state/state.types';
import type { FilterSection, Metadata } from '../models/search-result';
import type {
  VoyagerDocument,
  VoyagerSearchResponse,
} from './VoyagerSearchService.types';

const formatDateTime = (value?: string): string | null =>
  value
    ? new Date(value).toLocaleString('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

const formatLongDate = (value?: string): string | null =>
  value
    ? new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

export const determineRecordType = (format: string) => {
  const formatLower = format.toLowerCase();

  if (
    formatLower.includes('service') ||
    formatLower.includes('arcgis') ||
    formatLower.includes('wms') ||
    formatLower.includes('wfs')
  ) {
    return {
      name: 'service',
      color: '#28a745',
      icon: 'cloud',
    };
  }

  if (formatLower.includes('pdf') || formatLower.includes('text')) {
    return {
      name: 'document',
      color: '#6c757d',
      icon: 'file-text',
    };
  }

  if (
    formatLower.includes('image') ||
    formatLower.includes('jpeg') ||
    formatLower.includes('png')
  ) {
    return {
      name: 'image',
      color: '#17a2b8',
      icon: 'image',
    };
  }

  return {
    name: 'dataset',
    color: '#007bff',
    icon: 'database',
  };
};

export const transformDocument = (doc: VoyagerDocument): Metadata => {
  const acquiredDateRaw = doc.fd__acquired || doc.fd_acquired;
  const linkTypes = doc.download
    ? [
        {
          name: 'Download',
          description: 'Download file',
          href: doc.download,
          fileType: doc.format || 'unknown',
          color: '#0066cc',
          title: 'Download',
          beforeImg: null,
          beforeImgAlt: null,
          afterIcon: 'download',
        },
      ]
    : [];

  return {
    ...doc,
    title: doc.title || doc.name || '',
    defaultTitle: doc.title || doc.name || '',
    abstract: doc.abstract || doc.description || '',
    keywords: doc.keywords || [],
    'geonet:info': {
      uuid: doc.id,
      changeDate: doc.modified || '',
    },
    keywordGroup: '',
    ngdaTheme: null,
    publicationDate: formatDateTime(doc.fd_publish_date),
    modificationDate: formatLongDate(doc.modified),
    thumbnailUrl: doc.thumb || doc.path_to_thumb || null,
    event: doc.fs_event || null,
    acquiredDate: formatDateTime(acquiredDateRaw),
    platform: doc.fs_platform || null,
    upvotes: doc.likes || 0,
    downvotes: doc.dislikes || 0,
    status: '',
    status_text: '',
    statusTypes: [],
    type: doc.format_type || 'dataset',
    recordType: determineRecordType(doc.format || ''),
    responsibleParty: '',
    publisher: doc.fs__provider || '',
    link: doc.download || doc.fullpath || '',
    linkTypes,
    terriaMapViewrLink: null,
    standardName: doc.format || '',
    bbox: doc.bbox || null,
    geo: doc.geo || null,
  };
};

export const transformDocuments = (docs: VoyagerDocument[]): Metadata[] =>
  docs.map((doc) => transformDocument(doc));

export const transformResponse = (
  data: VoyagerSearchResponse,
  query: ResultListStateQuery,
  filterSections: FilterSection[] = [],
): ResultListState => ({
  id: 'voyager-search',
  isLoading: false,
  query,
  results: {
    count: data.response.numFound,
    data: transformDocuments(data.response.docs),
    filterSections,
    autocomplete: [],
  },
});
